import React, { useState, useEffect } from "react";
import { TrendingUp, Key, FileText, User, ArrowRight, DollarSign, BookOpen, Loader2, ChevronRight, Youtube, X } from "lucide-react";
import ReactECharts from 'echarts-for-react';
import { useSelector, useDispatch } from "react-redux";
import { getUserById, getUserDashboardData } from "../../services/authService";
import { useNavigate, useLocation } from "react-router-dom";
import OnboardingSection from "./OnboardingSection";
import TrialExpirationBanner from "./TrialExpirationBanner";
import { toast } from "react-toastify";
import { dismissOnboarding } from "../../store/slices/authSlice";

const Overview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, isAuthenticated, role } = useSelector((state) => state.auth);
  const onboardingActive = useSelector((state) => state.auth.onboardingActive);
  const dispatch = useDispatch();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [remainingDays, setRemainingDays] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const getUserDetails = async () => {
    try {
      const response = await getUserById(token);

      if (response?.tokenExpiration) {
        console.log(response);
        setUserDetails(response);
      } else {
        toast.error(response?.message || 'Failed to fetch user details');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error(error);
    }
  };

  // Calculate remaining trial days
  useEffect(() => {
    getUserDetails()
    if (!userDetails?.tokenExpiration) return;

    const now = new Date();
    const exp = new Date(userDetails.tokenExpiration);

    const diffTime = exp.getTime() - now.getTime();
    const diffDays = Math.max(
      0,
      Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    );

    setRemainingDays(diffDays);
  }, [userDetails?.tokenExpiration]);


  // Debug logging
  console.log("Overview component - Auth state:", { token, user, isAuthenticated, role });

  // Default data structure in case no data is provided
  const defaultData = {
    overview: {
      tokenBalance: { available: 0, label: "tokens available", isPro: false },
      apiCallsToday: { count: 0, growth: 0, growthLabel: "" },
      successRate: { percentage: 0, change: 0, changeLabel: "" },
      avgResponseTime: { time: 0, change: 0, changeLabel: "" }
    },
    usageChart: { period: "Last 7 Days", data: [], maxValue: 0 },
    topLanguages: { period: "This Week", languages: [], maxValue: 0 },
    recentActivity: { activities: [] },
    quickActions: { actions: [] }
  };

  // Fetch dashboard data function
  const fetchDashboardData = async (isRefresh = false) => {
    console.log("fetchDashboardData called with token:", token);

    if (!token) {
      console.error("No authentication token found");
      setError("No authentication token found");
      setLoading(false);
      return;
    }

    if (!isAuthenticated) {
      console.error("User not authenticated");
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const data = await getUserDashboardData(token);
      setDashboardData(data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to fetch dashboard data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const data = dashboardData || defaultData;

  // Colors for the 4 active services matching the mockup design
  const SERVICE_COLORS = {
    TTT: '#1e6c56', // Text-To-Text (Dark Green)
    TTS: '#c99718', // Text-To-Speech (Gold)
    STT: '#00b074', // Speech-To-Text (Vibrant Green)
    STS: '#ff8a3d', // Speech Translations (Orange)
  };

  // Dynamic backend service usage binding
  const serviceUsageData = data.serviceUsage || {};
  const hasServiceUsage = Object.values(serviceUsageData).some(v => v > 0);

  const donutData = hasServiceUsage ? [
    { value: serviceUsageData.TTT || 0, name: 'Text-To-Text', itemStyle: { color: SERVICE_COLORS.TTT } },
    { value: serviceUsageData.TTS || 0, name: 'Text-To-Speech', itemStyle: { color: SERVICE_COLORS.TTS } },
    { value: serviceUsageData.STT || 0, name: 'Speech-To-Text', itemStyle: { color: SERVICE_COLORS.STT } },
    { value: serviceUsageData.STS || 0, name: 'Speech Translations', itemStyle: { color: SERVICE_COLORS.STS } }
  ] : [
    { value: 45000, name: 'Text-To-Text', itemStyle: { color: SERVICE_COLORS.TTT } },
    { value: 28000, name: 'Text-To-Speech', itemStyle: { color: SERVICE_COLORS.TTS } },
    { value: 22000, name: 'Speech-To-Text', itemStyle: { color: SERVICE_COLORS.STT } },
    { value: 16000, name: 'Speech Translations', itemStyle: { color: SERVICE_COLORS.STS } }
  ];

  // ECharts configuration for Token Usage Donut Chart (Top Left Card)
  const tokenUsageDonutOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} tokens ({d}%)'
    },
    series: [
      {
        name: 'Token Usage',
        type: 'pie',
        radius: ['52%', '78%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#fff',
          borderWidth: 3
        },
        label: {
          show: false
        },
        data: donutData
      }
    ]
  };

  // Dynamic backend daily service usage binding for line chart
  const dailyUsageList = data.dailyServiceUsage || [];
  const hasDailyData = dailyUsageList.some(item => (item.TTT > 0 || item.TTS > 0 || item.STT > 0 || item.STS > 0));

  const daysList = hasDailyData
    ? dailyUsageList.map(item => item.day)
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const seriesTTT = hasDailyData ? dailyUsageList.map(item => item.TTT) : [1720, 1780, 1820, 1950, 2020, 2220, 2600];
  const seriesTTS = hasDailyData ? dailyUsageList.map(item => item.TTS) : [1620, 1680, 1750, 1850, 1920, 2120, 2000];
  const seriesSTT = hasDailyData ? dailyUsageList.map(item => item.STT) : [1380, 1450, 1520, 1620, 1680, 1880, 2350];
  const seriesSTS = hasDailyData ? dailyUsageList.map(item => item.STS) : [1020, 1080, 1140, 1250, 1310, 1500, 1700];

  const maxChartValue = hasDailyData
    ? Math.ceil(Math.max(...dailyUsageList.flatMap(i => [i.TTT, i.TTS, i.STT, i.STS]), 10) * 1.2)
    : 2500;

  const apiUsageLineOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: '#E5E7EB', type: 'dashed' } }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '5%',
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: daysList,
      axisLine: { lineStyle: { color: '#E5E7EB' } },
      axisTick: { show: false },
      axisLabel: { color: '#6B7280', fontSize: 12 }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: maxChartValue,
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#F3F4F6' } },
      axisLabel: {
        color: '#9CA3AF',
        fontSize: 11,
        formatter: (val) => val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      }
    },
    series: [
      {
        name: 'TTT',
        type: 'line',
        smooth: 0.45,
        symbol: 'none',
        lineStyle: { width: 2.5, color: SERVICE_COLORS.TTT },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(30, 108, 86, 0.22)' },
              { offset: 1, color: 'rgba(30, 108, 86, 0.01)' }
            ]
          }
        },
        data: seriesTTT
      },
      {
        name: 'TTS',
        type: 'line',
        smooth: 0.45,
        symbol: 'none',
        lineStyle: { width: 2.5, color: SERVICE_COLORS.TTS },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(201, 151, 24, 0.22)' },
              { offset: 1, color: 'rgba(201, 151, 24, 0.01)' }
            ]
          }
        },
        data: seriesTTS
      },
      {
        name: 'STT',
        type: 'line',
        smooth: 0.45,
        symbol: 'none',
        lineStyle: { width: 2.5, color: SERVICE_COLORS.STT },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(0, 176, 116, 0.22)' },
              { offset: 1, color: 'rgba(0, 176, 116, 0.01)' }
            ]
          }
        },
        data: seriesSTT
      },
      {
        name: 'STS',
        type: 'line',
        smooth: 0.45,
        symbol: 'none',
        lineStyle: { width: 2.5, color: SERVICE_COLORS.STS },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(255, 138, 61, 0.22)' },
              { offset: 1, color: 'rgba(255, 138, 61, 0.01)' }
            ]
          }
        },
        data: seriesSTS
      }
    ]
  };

  const topLanguagePairs = (data.topLanguagePairs && data.topLanguagePairs.length > 0)
    ? data.topLanguagePairs
    : [
      { pair: "Yoruba To English", count: "45,200", change: "+15%", percentage: 80 },
      { pair: "Swahili To English", count: "32,000", change: "+12%", percentage: 62 },
      { pair: "Nigerian Pidgin To English", count: "28,900", change: "+8%", percentage: 50 },
      { pair: "English To Yoruba", count: "15,300", change: "+5%", percentage: 30 },
      { pair: "Other Combinations", count: "8,500", change: "+3%", percentage: 18 },
    ];


  // Check if user is authenticated
  if (!isAuthenticated || !token) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600 mb-4">Please log in to access your dashboard.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {userDetails?.purchaseHistory?.length == 0 && <TrialExpirationBanner user={userDetails} />}

      {/* Onboarding Section - Show before stats when user signs up */}
      {onboardingActive && (
        <OnboardingSection
          onDismiss={() => {
            dispatch(dismissOnboarding());
          }}
        />
      )}


      {/* Top Row Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Token Balance Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Token Balance</h3>

            <span className={`text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full tracking-wider border ${remainingDays === null || remainingDays === 0
              ? "bg-gray-50 border-gray-200 text-gray-400"
              : remainingDays >= 1 && remainingDays <= 5
                ? "bg-red-50 border-red-200 text-red-600"
                : remainingDays >= 6 && remainingDays <= 14
                  ? "bg-[#FFF8EB] border-[#FFE7C1] text-[#EF8702]"
                  : "bg-[#E7F3EF] border-[#D0E7DF] text-[#1E6C56]"
              }`}>
              {remainingDays === null || remainingDays === 0
                ? "expired"
                : `expires in ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`
              }
            </span>

          </div>
          <div className="mb-4">
            <p className="text-3xl font-bold text-gray-900">
              {data.overview.tokenBalance.available.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">{data.overview.tokenBalance.label}</p>
          </div>
          <button onClick={() => navigate("/user-dashboard/upgrade-plan")} className="w-full bg-secondary text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors">
            Buy More Tokens
          </button>
        </div>

        {/* API Calls Today Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">API Calls Today</h3>
          <div className="mb-4">
            <p className="text-3xl font-bold text-gray-900">
              {data.overview.apiCallsToday.count.toLocaleString()}
            </p>
            <p className={`text-sm flex items-center gap-1 ${data.overview.apiCallsToday.growth >= 0 ? 'text-secondary' : 'text-red-500'
              }`}>
              <TrendingUp className={`w-4 h-4 ${data.overview.apiCallsToday.growth < 0 ? 'rotate-180' : ''}`} />
              {data.overview.apiCallsToday.growthLabel}
            </p>
          </div>
          <button onClick={() => navigate("/user-dashboard/developer-tools")} className="w-full border border-secondary text-secondary py-2 px-4 rounded-lg font-medium hover:bg-green-50 transition-colors">
            Generate API Key
          </button>
        </div>
      </div>

      {/* Middle Row Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Success Rate Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Success Rate</h3>
          <div>
            <p className="text-3xl font-bold text-gray-900">{data.overview.successRate.percentage}%</p>
            {/* <p className={`text-sm flex items-center gap-1 ${data.overview.successRate.change >= 0 ? 'text-secondary' : 'text-red-500'
              }`}>
              <TrendingUp className={`w-4 h-4 ${data.overview.successRate.change < 0 ? 'rotate-180' : ''}`} />
              {data.overview.successRate.changeLabel}
            </p> */}
          </div>
        </div>

        {/* Avg Response Time Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Avg Response Time</h3>
          <div>
            <p className="text-3xl font-bold text-gray-900">
              {data.overview.avgResponseTime.time === 0 ? '0ms' : `${data.overview.avgResponseTime.time}ms`}
            </p>
            {/* <p className={`text-sm flex items-center gap-1 ${data.overview.avgResponseTime.change <= 0 ? 'text-secondary' : 'text-red-500'
              }`}>
              <TrendingUp className={`w-4 h-4 ${data.overview.avgResponseTime.change > 0 ? 'rotate-180' : ''}`} />
              {data.overview.avgResponseTime.changeLabel}
            </p> */}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="space-y-6">
        {/* Top Row: Token Usage Donut Chart + Top Language Pairs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1: Token Usage Across All Services */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Token Usage Across All Services</h3>

            <div className="h-64 flex items-center justify-center">
              <ReactECharts
                option={tokenUsageDonutOption}
                style={{ height: '100%', width: '100%' }}
              />
            </div>

            {/* Donut Legend */}
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 pt-2 text-xs font-semibold text-gray-700">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#1e6c56]" />
                <span>Text-To-Text</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#c99718]" />
                <span>Text-To-Speech</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#00b074]" />
                <span>Speech-To-Text</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#ff8a3d]" />
                <span>Speech Translations</span>
              </div>
            </div>
          </div>

          {/* Card 2: Top Language Pairs Across All Services */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-5">Top Language Pairs Across All Services</h3>

            <div className="space-y-5">
              {topLanguagePairs.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-900">{item.pair}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-600">{item.count}</span>
                      <span className="bg-emerald-50 text-emerald-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {item.change}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-[#1e6c56] h-full rounded-full transition-all duration-300"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Card 3: API Usage (Last 7 Days) - Full Width Line Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h3 className="text-xl font-bold text-gray-900">API Usage (Last 7 Days)</h3>
            <div className="flex items-center gap-6 text-xs font-semibold text-gray-600 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#1e6c56]" />
                <span>TTT</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#c99718]" />
                <span>TTS</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#00b074]" />
                <span>STT</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#ff8a3d]" />
                <span>STS</span>
              </div>
            </div>
          </div>

          <div className="h-80">
            <ReactECharts
              option={apiUsageLineOption}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* Bottom Row - Quick Actions and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {/* Inject the Watch Onboarding action if not present */}
            {[
              ...data.quickActions.actions,
              ...(!data.quickActions.actions.some(a => a.icon === 'youtube') ? [{
                id: 'watch-onboarding',
                title: 'Watch Onboarding Video',
                description: 'Learn how to use NKENNEAi API in 2 minutes',
                icon: 'youtube',
                color: 'red'
              }] : [])
            ].map((action, index) => {
              const getIcon = (iconType, color) => {
                switch (iconType) {
                  case 'key':
                    return <Key className="w-5 h-5 text-orange-500" />;
                  case 'dollar':
                    return <DollarSign className="w-5 h-5 text-green-500" />;
                  case 'document':
                    return <BookOpen className="w-5 h-5 text-yellow-500" />;
                  case 'user':
                    return <User className="w-5 h-5 text-purple-500" />;
                  case 'youtube':
                    return <Youtube className="w-5 h-5 text-red-600" />;
                  default:
                    return <div className="w-5 h-5 bg-gray-500 rounded-full"></div>;
                }
              };

              // Map action to route based on icon type or title
              const getActionRoute = (action) => {
                // If action has a route property, use it
                if (action.route) {
                  return action.route;
                }

                // Map based on icon type
                switch (action.icon) {
                  case 'key':
                    return '/user-dashboard/developer-tools';
                  case 'dollar':
                    return '/user-dashboard/upgrade-plan';
                  case 'document':
                    return '/user-dashboard/developer-tools';
                  case 'user':
                    return '/user-dashboard/profile';
                  default:
                    // Fallback: map based on title keywords
                    const title = action.title?.toLowerCase() || '';
                    if (title.includes('api key') || title.includes('developer')) {
                      return '/user-dashboard/developer-tools';
                    }
                    if (title.includes('token') || title.includes('purchase') || title.includes('buy') || title.includes('upgrade')) {
                      return '/user-dashboard/upgrade-plan';
                    }
                    if (title.includes('profile') || title.includes('account') || title.includes('settings')) {
                      return '/user-dashboard/profile';
                    }
                    if (title.includes('support') || title.includes('help') || title.includes('documentation')) {
                      return '/user-dashboard/support';
                    }
                    if (title.includes('purchase history') || title.includes('history')) {
                      return '/user-dashboard/purchase-history';
                    }
                    return null;
                }
              };

              const handleActionClick = () => {
                if (action.icon === 'youtube' || action.title === 'Watch Onboarding Video') {
                  setShowOnboardingModal(true);
                  return;
                }
                const route = getActionRoute(action);
                if (route) {
                  navigate(route);
                }
              };

              return (
                <div
                  key={action.id || index}
                  onClick={handleActionClick}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {getIcon(action.icon, action.color)}
                    <div>
                      <span className="text-gray-700 font-medium">{action.title}</span>
                      {action.description && (
                        <p className="text-xs text-gray-500">{action.description}</p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-h-[450px] overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {data.recentActivity.activities.length > 0 ? (
              data.recentActivity.activities.map((activity, index) => (
                <div key={activity.id || index} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${activity.status === 'success' ? 'bg-green-500' : 'bg-orange-500'
                    }`}></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                    <p className="text-xs text-gray-500">{activity.timeAgo}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Onboarding Modal */}
      {showOnboardingModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowOnboardingModal(false)}
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">

            <div className="max-h-[80vh] overflow-y-auto">
              <OnboardingSection isQuickOnboardingMode={true} onDismiss={() => setShowOnboardingModal(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
